import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaUtensils,
    FaMobileAlt,
    FaChild,
    FaConciergeBell,
    FaSyncAlt,
    FaChevronDown,
} from 'react-icons/fa';

import { fetchRestaurantOrders, fetchCateringActiveOrders } from './orderApi';
import { isOrderCreatedToday } from './storeOrderDateUtils';
import {
    getStatusMeta,
    resolveStatusKey,
    isTerminalStatus,
} from './ui/orderStatusFlow';
import {
    SERVICE_TYPE_BY_MENU_CHANNEL,
    isServiceFilteringActive,
} from './restaurantServiceAccess';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import './css/DineInActiveOrders.css';

/**
 * Glanceable strip of TODAY's active online orders shown on the POS screen.
 * Channels are limited to services the restaurant has opted into.
 */

const CHANNELS = [
    { key: 'dinein', label: 'Dine-In', icon: FaUtensils, orderType: 'offline', source: 'MB' },
    { key: 'myeats', label: 'My Eats', icon: FaMobileAlt, orderType: 'myeats', source: 'MYEATS' },
    { key: 'jreats', label: 'Jr Eats', icon: FaChild, orderType: 'jreats', source: 'JREATS' },
    { key: 'catering', label: 'Catering', icon: FaConciergeBell, orderType: 'catering', catering: true },
];

// Display order for the status chips.
const STATUS_ORDER = [
    'NEW',
    'ACCEPTED',
    'PREPARING',
    'READY',
    'OFD',
    'PICKEDUP',
    'SERVED',
    'DELIVERED',
    'COMPLETED',
    'UNDELIVERED',
    'CANCELLED',
    'UNKNOWN',
];

function rawStatus(order) {
    return order?.orderStatus ?? order?.OrderStatus ?? order?.order_status ?? '';
}

function activeOnly(list) {
    return (Array.isArray(list) ? list : []).filter(
        (o) => !isTerminalStatus(rawStatus(o)) && isOrderCreatedToday(o)
    );
}

function countByStatus(orders, channelKey) {
    const counts = {};
    orders.forEach((o) => {
        let key = resolveStatusKey(rawStatus(o));
        if (channelKey === 'myeats' && key === 'ACCEPTED') {
            key = 'NEW';
        }
        counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
}

function statusOrderForChannel(channelKey) {
    if (channelKey === 'myeats') {
        return STATUS_ORDER.filter((k) => k !== 'ACCEPTED');
    }
    return STATUS_ORDER;
}

function channelEnabled(channel, enabledTypes) {
    if (!isServiceFilteringActive(enabledTypes)) return false;
    const serviceType = SERVICE_TYPE_BY_MENU_CHANNEL[channel.key];
    if (!serviceType) return true;
    return enabledTypes.includes(serviceType);
}

const DineInActiveOrders = ({ restaurantId }) => {
    const navigate = useNavigate();
    const { enabledTypes, loading: servicesLoading } = useRestaurantEnabledServices();
    const [data, setData] = useState({ dinein: [], myeats: [], jreats: [], catering: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [collapsed, setCollapsed] = useState(true);

    const visibleChannels = useMemo(
        () => (servicesLoading ? [] : CHANNELS.filter((ch) => channelEnabled(ch, enabledTypes))),
        [enabledTypes, servicesLoading]
    );

    // Open the Orders view for a channel, optionally pre-filtered to one status.
    const openOrders = useCallback(
        (orderType, statusKey) => {
            const base = `/home?tab=orders&orderType=${orderType}`;
            navigate(statusKey ? `${base}&status=${statusKey}` : base);
        },
        [navigate]
    );

    const load = useCallback(async () => {
        if (!restaurantId || servicesLoading || !isServiceFilteringActive(enabledTypes)) return;
        setLoading(true);
        setError('');
        try {
            const wants = {
                dinein: enabledTypes.includes('DINEIN'),
                myeats: enabledTypes.includes('MYEATS'),
                jreats: enabledTypes.includes('JREATS'),
                catering: enabledTypes.includes('CATERING'),
            };
            const [dinein, myeats, jreats, catering] = await Promise.all([
                wants.dinein
                    ? fetchRestaurantOrders(restaurantId, { orderSource: 'MB' }).catch(() => [])
                    : Promise.resolve([]),
                wants.myeats
                    ? fetchRestaurantOrders(restaurantId, { orderSource: 'MYEATS' }).catch(() => [])
                    : Promise.resolve([]),
                wants.jreats
                    ? fetchRestaurantOrders(restaurantId, { orderSource: 'JREATS' }).catch(() => [])
                    : Promise.resolve([]),
                wants.catering
                    ? fetchCateringActiveOrders(restaurantId).catch(() => [])
                    : Promise.resolve([]),
            ]);
            setData({
                dinein: activeOnly(dinein),
                myeats: activeOnly(myeats),
                jreats: activeOnly(jreats),
                catering: activeOnly(catering),
            });
        } catch (e) {
            setError('Could not load live order counts.');
        } finally {
            setLoading(false);
        }
    }, [restaurantId, enabledTypes, servicesLoading]);

    useEffect(() => {
        load();
    }, [load]);

    // Light auto-refresh so the kitchen always sees current counts.
    useEffect(() => {
        if (!restaurantId) return undefined;
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [restaurantId, load]);

    if (!restaurantId || servicesLoading || visibleChannels.length === 0) return null;

    return (
        <div
            className={`dinein-orders-summary${
                collapsed ? ' dinein-orders-summary--collapsed' : ''
            }`}
        >
            <div className="dinein-orders-summary__head">
                <button
                    type="button"
                    className="dinein-orders-summary__collapse"
                    onClick={() => setCollapsed((value) => !value)}
                    aria-expanded={!collapsed}
                >
                    <span className="dinein-orders-summary__title">
                        Today&rsquo;s active orders
                    </span>
                    <FaChevronDown
                        className="dinein-orders-summary__collapse-icon"
                        aria-hidden="true"
                    />
                </button>
                <div className="dinein-orders-summary__head-actions">
                    <button
                        type="button"
                        className="dinein-orders-summary__refresh"
                        onClick={load}
                        disabled={loading}
                        aria-label="Refresh order counts"
                        title="Refresh"
                    >
                        <FaSyncAlt className={loading ? 'is-spinning' : ''} />
                    </button>
                </div>
            </div>

            <div className="dinein-orders-summary__channels">
                {visibleChannels.map((ch) => {
                    const orders = data[ch.key] || [];
                    const counts = countByStatus(orders, ch.key);
                    const Icon = ch.icon;
                    const statusKeys = statusOrderForChannel(ch.key).filter((k) => counts[k]);
                    return (
                        <div key={ch.key} className="dinein-channel-card">
                            <button
                                type="button"
                                className="dinein-channel-card__header"
                                onClick={() => openOrders(ch.orderType)}
                                title={`Open ${ch.label} orders`}
                            >
                                <Icon className="dinein-channel-card__icon" aria-hidden="true" />
                                <span className="dinein-channel-card__name">{ch.label}</span>
                                <span className="dinein-channel-card__total">{orders.length}</span>
                            </button>
                            <div className="dinein-channel-card__statuses">
                                {statusKeys.length === 0 ? (
                                    <span className="dinein-status-line dinein-status-line--empty">
                                        No active orders
                                    </span>
                                ) : (
                                    statusKeys.map((k) => {
                                        const meta = getStatusMeta(k);
                                        return (
                                            <button
                                                key={k}
                                                type="button"
                                                className="dinein-status-line"
                                                onClick={() => openOrders(ch.orderType, k)}
                                                title={`Open ${ch.label} – ${meta.label}`}
                                            >
                                                <span
                                                    className="dinein-status-line__dot"
                                                    style={{ backgroundColor: meta.color }}
                                                    aria-hidden="true"
                                                />
                                                <span className="dinein-status-line__label">{meta.label}</span>
                                                <span
                                                    className="dinein-status-line__count"
                                                    style={{ color: meta.color, backgroundColor: meta.bg }}
                                                >
                                                    {counts[k]}
                                                </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {error ? (
                <div className="dinein-orders-summary__error">{error}</div>
            ) : null}
        </div>
    );
};

export default DineInActiveOrders;
